import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase-config';
import { SecureLog, PaymentEvents } from '../utils/SecurityLogger';
import THEME from '../theme';
import { motion, AnimatePresence } from 'framer-motion';

// ======================== SECURITY HELPERS ========================

// [SECURITY] Validate order_id format — 20-char alphanumeric expected from Juspay
const validateOrderId = (id) => {
    if (!id || id === 'N/A') return false;
    // Allow alpha-numeric, underscores, hyphens (typical payment gateway IDs)
    return /^[a-zA-Z0-9_-]{8,64}$/.test(id);
};

// ======================== STATUS CONFIGURATIONS ========================

const STATUS_CONFIG = {
    success: {
        title: 'Payment Successful!',
        subtitle: 'Your payment has been processed successfully.',
        color: '#14B8A6',
        bgColor: 'rgba(20, 184, 166, 0.1)',
        borderColor: 'rgba(20, 184, 166, 0.3)',
        chipBg: 'rgba(20, 184, 166, 0.15)',
        chipText: '#5EEAD4',
        chipLabel: 'CHARGED',
        iconPath: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
        iconAnimation: 'success',
    },
    pending: {
        title: 'Payment Pending',
        subtitle: 'Your payment is being processed. This may take a few minutes.',
        color: '#F59E0B',
        bgColor: 'rgba(245, 158, 11, 0.1)',
        borderColor: 'rgba(245, 158, 11, 0.3)',
        chipBg: 'rgba(245, 158, 11, 0.15)',
        chipText: '#FCD34D',
        chipLabel: 'PENDING',
        iconPath: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
        iconAnimation: 'pending',
    },
    failed: {
        title: 'Payment Failed',
        subtitle: 'Your payment could not be processed. Please try again.',
        color: '#EF4444',
        bgColor: 'rgba(239, 68, 68, 0.1)',
        borderColor: 'rgba(239, 68, 68, 0.3)',
        chipBg: 'rgba(239, 68, 68, 0.15)',
        chipText: '#FCA5A5',
        chipLabel: 'FAILED',
        iconPath: 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z',
        iconAnimation: 'failed',
    },
};

// ======================== ANIMATION VARIANTS ========================

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.08, delayChildren: 0.5 }
    }
};

const itemVariants = {
    hidden: { opacity: 0, x: -16 },
    visible: { opacity: 1, x: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } }
};

const confettiColors = ['#F59E0B', '#14B8A6', '#818CF8', '#F472B6', '#34D399', '#FBBF24', '#A78BFA', '#FB923C'];

// ======================== ANIMATED STATUS ICON ========================

const AnimatedStatusIcon = ({ config }) => {
    const { iconAnimation, color, iconPath } = config;

    if (iconAnimation === 'success') {
        return (
            <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 200, damping: 14, delay: 0.2 }}
            >
                <svg className="w-20 h-20" viewBox="0 0 24 24" fill="none" style={{ color }}>
                    {/* Circle */}
                    <motion.circle
                        cx="12" cy="12" r="10"
                        stroke="currentColor" strokeWidth="1.5" fill="none"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                    />
                    {/* Checkmark */}
                    <motion.path
                        d="M8 12l3 3 5-6"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.4, delay: 0.8 }}
                    />
                </svg>
            </motion.div>
        );
    }

    if (iconAnimation === 'failed') {
        return (
            <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: [0, 1.15, 0.95, 1] }}
                transition={{ duration: 0.5, delay: 0.2 }}
            >
                <motion.div
                    animate={{ x: [0, -4, 4, -3, 3, 0] }}
                    transition={{ duration: 0.4, delay: 0.7 }}
                >
                    <svg className="w-20 h-20" viewBox="0 0 24 24" fill="none" style={{ color }}>
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
                        <motion.path
                            d="M15 9l-6 6M9 9l6 6"
                            stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{ duration: 0.3, delay: 0.6 }}
                        />
                    </svg>
                </motion.div>
            </motion.div>
        );
    }

    // Pending — clock animation
    return (
        <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 14, delay: 0.2 }}
        >
            <svg className="w-20 h-20" viewBox="0 0 24 24" fill="none" style={{ color }}>
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
                <motion.path
                    d="M12 6v6l4 2"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                />
            </svg>
            <motion.div
                className="absolute inset-0"
                animate={{ rotate: 360 }}
                transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                style={{ border: `2px dashed ${color}30`, borderRadius: '50%' }}
            />
        </motion.div>
    );
};

// ======================== CONFETTI BURST ========================

const ConfettiBurst = ({ show }) => {
    if (!show) return null;

    return (
        <div className="fixed inset-0 pointer-events-none z-[60] overflow-hidden">
            {Array.from({ length: 24 }).map((_, i) => {
                const angle = (i / 24) * Math.PI * 2;
                const distance = 120 + Math.random() * 180;
                const size = 4 + Math.random() * 6;
                return (
                    <motion.div
                        key={i}
                        initial={{ x: '50vw', y: '40vh', scale: 0, opacity: 1 }}
                        animate={{
                            x: `calc(50vw + ${Math.cos(angle) * distance}px)`,
                            y: `calc(40vh + ${Math.sin(angle) * distance}px)`,
                            scale: [0, 1.5, 0.5],
                            opacity: [1, 1, 0],
                            rotate: Math.random() * 360,
                        }}
                        transition={{ duration: 0.8 + Math.random() * 0.5, delay: i * 0.02, ease: 'easeOut' }}
                        className="absolute rounded-full"
                        style={{
                            width: size,
                            height: size,
                            backgroundColor: confettiColors[i % confettiColors.length],
                        }}
                    />
                );
            })}
        </div>
    );
};

// ======================== MAIN COMPONENT ========================

function PaymentStatus() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [verifiedStatus, setVerifiedStatus] = useState(null);
    const [pollCount, setPollCount] = useState(0);
    const [showConfetti, setShowConfetti] = useState(false);
    const [copied, setCopied] = useState(false);
    const pollTimerRef = useRef(null);
    const hasLoggedRef = useRef(false);

    const orderId = searchParams.get('order_id') || 'N/A';
    const rawStatus = (searchParams.get('payment_status') || '').toUpperCase();

    // [SECURITY] Validate order_id
    const isValidOrderId = validateOrderId(orderId);

    // Map raw status to our keys
    const statusKey = verifiedStatus
        || (rawStatus === 'SUCCESS' || rawStatus === 'CHARGED'
            ? 'success'
            : rawStatus === 'PENDING' || rawStatus === 'PENDING_VBV'
                ? 'pending'
                : 'failed');

    const config = STATUS_CONFIG[statusKey];

    // Log payment outcome — only once
    useEffect(() => {
        if (hasLoggedRef.current) return;
        hasLoggedRef.current = true;

        if (!isValidOrderId) {
            SecureLog.warn('[PaymentStatus] Invalid order_id format:', orderId);
        }

        if (statusKey === 'success') PaymentEvents.success(orderId);
        else if (statusKey === 'pending') PaymentEvents.pending(orderId);
        else PaymentEvents.failed(orderId, rawStatus);
    }, [statusKey, orderId, rawStatus, isValidOrderId]);

    // Fetch user data
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                try {
                    const userDocRef = doc(db, 'users', currentUser.uid);
                    const userDoc = await getDoc(userDocRef);
                    if (userDoc.exists()) {
                        setUserData(userDoc.data());
                    }
                } catch (err) {
                    SecureLog.error('Failed to fetch user data:', err);
                }
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // [SECURITY] Backend verification — verify payment status with server
    const verifyWithBackend = useCallback(async () => {
        if (!isValidOrderId) return null;
        try {
            const user = getAuth().currentUser;
            if (!user) return null;
            const token = await user.getIdToken();

            // Try to verify via your backend API
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL || ''}/api/payment/verify?order_id=${encodeURIComponent(orderId)}`,
                {
                    headers: { 'Authorization': `Bearer ${token}` },
                    signal: AbortSignal.timeout(8000), // 8s timeout
                }
            );

            if (!response.ok) {
                SecureLog.warn('[PaymentStatus] Backend verification failed:', response.status);
                return null;
            }

            const data = await response.json();
            if (data.status) {
                const mapped = data.status === 'CHARGED' ? 'success'
                    : data.status === 'PENDING' || data.status === 'PENDING_VBV' ? 'pending'
                        : 'failed';
                return mapped;
            }
            return null;
        } catch (err) {
            // Backend may not have this endpoint yet — silently fall back to URL params
            SecureLog.warn('[PaymentStatus] Backend verify unavailable:', err.message);
            return null;
        }
    }, [orderId, isValidOrderId]);

    // [FEATURE] Auto-poll for pending status — every 10s for up to 2 minutes
    useEffect(() => {
        if (statusKey !== 'pending' || pollCount >= 12) return;

        pollTimerRef.current = setInterval(async () => {
            const verified = await verifyWithBackend();
            if (verified && verified !== 'pending') {
                setVerifiedStatus(verified);
                clearInterval(pollTimerRef.current);
                if (verified === 'success') {
                    setShowConfetti(true);
                    setTimeout(() => setShowConfetti(false), 2500);
                }
            }
            setPollCount(prev => {
                if (prev >= 11) {
                    clearInterval(pollTimerRef.current);
                }
                return prev + 1;
            });
        }, 10000);

        return () => clearInterval(pollTimerRef.current);
    }, [statusKey, pollCount, verifyWithBackend]);

    // Try initial backend verification on mount
    useEffect(() => {
        const doVerify = async () => {
            const verified = await verifyWithBackend();
            if (verified) {
                setVerifiedStatus(verified);
                if (verified === 'success') {
                    setShowConfetti(true);
                    setTimeout(() => setShowConfetti(false), 2500);
                }
            }
        };
        doVerify();
    }, [verifyWithBackend]);

    // [FEATURE] Share/copy receipt details
    const handleShareReceipt = useCallback(async () => {
        const receiptText = [
            `Resido Payment Receipt`,
            `─────────────────────`,
            `Order ID: ${orderId}`,
            `Status: ${config.chipLabel}`,
            `Name: ${userData?.name || 'N/A'}`,
            `Date: ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`,
            `─────────────────────`,
            `Powered by Resido`,
        ].join('\n');

        if (navigator.share) {
            try {
                await navigator.share({ title: 'Resido Payment Receipt', text: receiptText });
            } catch (err) {
                if (err.name !== 'AbortError') SecureLog.warn('Share failed:', err);
            }
        } else {
            // Fallback: copy to clipboard
            try {
                await navigator.clipboard.writeText(receiptText);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch (err) {
                SecureLog.warn('Copy failed:', err);
            }
        }
    }, [orderId, config, userData]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: THEME.colors.midnightBlue }}>
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500 mx-auto mb-4"></div>
                    <p className="text-slate-400 text-sm">Verifying payment...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center p-4"
            style={{ backgroundColor: THEME.colors.midnightBlue, fontFamily: THEME.fonts.body }}>

            {/* [ANIMATION] Confetti burst for success */}
            <ConfettiBurst show={showConfetti} />

            <motion.div
                className="w-full max-w-md"
                initial={{ opacity: 0, y: 50, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ type: 'spring', duration: 0.8, bounce: 0.4 }}
            >
                {/* Receipt Card */}
                <motion.div
                    className="rounded-3xl border overflow-hidden shadow-2xl relative"
                    initial={{ height: 0 }}
                    animate={{ height: 'auto' }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    style={{ backgroundColor: THEME.colors.midnightSurface, borderColor: THEME.colors.borderColor }}
                >
                    {/* Top ticket decoration */}
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 w-8 h-6 rounded-b-full shadow-inner" style={{ backgroundColor: THEME.colors.midnightBlue }}></div>
                    <div className="absolute top-1/2 -left-3 transform -translate-y-1/2 w-6 h-6 rounded-full shadow-inner" style={{ backgroundColor: THEME.colors.midnightBlue }}></div>
                    <div className="absolute top-1/2 -right-3 transform -translate-y-1/2 w-6 h-6 rounded-full shadow-inner" style={{ backgroundColor: THEME.colors.midnightBlue }}></div>

                    {/* [ANIMATION] Status Icon Header — animated SVG icons */}
                    <div className="pt-10 pb-6 flex flex-col items-center relative"
                        style={{ background: `linear-gradient(180deg, ${config.bgColor} 0%, transparent 100%)` }}>
                        <AnimatedStatusIcon config={config} />
                        <motion.h1
                            style={{ fontFamily: THEME.fonts.heading, color: config.color }}
                            className="text-2xl font-bold mt-4 text-center"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6 }}
                        >
                            {config.title}
                        </motion.h1>
                        <motion.p
                            className="text-slate-400 text-sm mt-2 text-center px-6 leading-relaxed"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.8 }}
                        >
                            {config.subtitle}
                        </motion.p>
                    </div>

                    {/* Dashed Divider */}
                    <div className="mx-6">
                        <div className="border-t-2 border-dashed" style={{ borderColor: THEME.colors.borderColor }}></div>
                    </div>

                    {/* Receipt Details */}
                    <motion.div
                        className="p-6 space-y-4"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        {/* [SECURITY] Order ID with validation warning */}
                        <motion.div variants={itemVariants} className="flex justify-between items-center">
                            <span className="text-slate-500 text-sm font-medium">Order ID</span>
                            <div className="flex items-center gap-2">
                                {!isValidOrderId && (
                                    <span className="text-red-400 text-[10px] font-medium">⚠️</span>
                                )}
                                <span className="text-white text-sm font-mono font-medium max-w-[180px] truncate">
                                    {orderId}
                                </span>
                            </div>
                        </motion.div>

                        {/* Status */}
                        <motion.div variants={itemVariants} className="flex justify-between items-center">
                            <span className="text-slate-500 text-sm font-medium">Status</span>
                            <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider"
                                style={{ backgroundColor: config.chipBg, color: config.chipText }}>
                                {config.chipLabel}
                            </span>
                        </motion.div>

                        {/* Name */}
                        {userData?.name && (
                            <motion.div variants={itemVariants} className="flex justify-between items-center">
                                <span className="text-slate-500 text-sm font-medium">Name</span>
                                <span className="text-white text-sm font-medium">{userData.name}</span>
                            </motion.div>
                        )}

                        {/* Institute */}
                        {userData?.instituteName && (
                            <motion.div variants={itemVariants} className="flex justify-between items-center">
                                <span className="text-slate-500 text-sm font-medium">Institute</span>
                                <span className="text-slate-300 text-sm">{userData.instituteName}</span>
                            </motion.div>
                        )}

                        {/* Timestamp */}
                        <motion.div variants={itemVariants} className="flex justify-between items-center">
                            <span className="text-slate-500 text-sm font-medium">Date</span>
                            <span className="text-slate-300 text-sm">
                                {new Date().toLocaleDateString('en-IN', {
                                    day: '2-digit', month: 'short', year: 'numeric',
                                    hour: '2-digit', minute: '2-digit'
                                })}
                            </span>
                        </motion.div>

                        {/* [FEATURE] Pending auto-poll indicator */}
                        {statusKey === 'pending' && pollCount > 0 && pollCount < 12 && (
                            <motion.div
                                variants={itemVariants}
                                className="flex items-center gap-2 pt-2"
                            >
                                <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></div>
                                <span className="text-amber-400/70 text-xs">
                                    Auto-checking status... ({pollCount}/12)
                                </span>
                            </motion.div>
                        )}
                    </motion.div>

                    {/* Dashed Divider */}
                    <div className="mx-6">
                        <div className="border-t-2 border-dashed" style={{ borderColor: THEME.colors.borderColor }}></div>
                    </div>

                    {/* Action Buttons */}
                    <div className="p-6 space-y-3">
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="w-full py-3.5 rounded-xl font-bold text-sm transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] shadow-lg"
                            style={{
                                backgroundColor: THEME.colors.goldAccent,
                                color: THEME.colors.midnightBlue,
                                boxShadow: '0 4px 14px rgba(245, 158, 11, 0.3)'
                            }}
                        >
                            Go to Dashboard
                        </button>

                        {/* [FEATURE] Share Receipt button — success only */}
                        {statusKey === 'success' && (
                            <button
                                onClick={handleShareReceipt}
                                className="w-full py-3.5 rounded-xl font-bold text-sm transition-all duration-200 border hover:bg-slate-800/50 flex items-center justify-center gap-2"
                                style={{ borderColor: THEME.colors.tealAccent + '40', color: THEME.colors.tealAccent }}
                            >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                </svg>
                                <AnimatePresence mode="wait">
                                    {copied ? (
                                        <motion.span key="copied" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                                            Copied to Clipboard ✓
                                        </motion.span>
                                    ) : (
                                        <motion.span key="share" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                                            Share Receipt
                                        </motion.span>
                                    )}
                                </AnimatePresence>
                            </button>
                        )}

                        {statusKey === 'failed' && (
                            <button
                                onClick={() => navigate('/dashboard')}
                                className="w-full py-3.5 rounded-xl font-bold text-sm transition-all duration-200 border hover:bg-slate-800/50"
                                style={{ borderColor: THEME.colors.borderColor, color: THEME.colors.textSecondary }}
                            >
                                Retry Payment
                            </button>
                        )}

                        {statusKey === 'pending' && (
                            <button
                                onClick={() => window.location.reload()}
                                className="w-full py-3.5 rounded-xl font-bold text-sm transition-all duration-200 border hover:bg-slate-800/50 flex items-center justify-center gap-2"
                                style={{ borderColor: THEME.colors.borderColor, color: THEME.colors.textSecondary }}
                            >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                Check Status
                            </button>
                        )}
                    </div>
                </motion.div>

                {/* Footer Note */}
                <motion.p
                    className="text-center text-slate-600 text-xs mt-6 px-4 leading-relaxed"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.2 }}
                >
                    {statusKey === 'pending'
                        ? 'Your payment status will be updated automatically. You can safely return to the dashboard.'
                        : statusKey === 'success'
                            ? 'Your bill will be marked as paid shortly. Thank you!'
                            : 'If money was deducted, it will be refunded within 5-7 business days.'}
                </motion.p>

                {/* [SECURITY] Invalid order_id warning */}
                {!isValidOrderId && orderId !== 'N/A' && (
                    <motion.p
                        className="text-center text-red-400/60 text-xs mt-3 px-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.5 }}
                    >
                        ⚠️ Order ID format is unexpected. If you were redirected here, please contact support.
                    </motion.p>
                )}
            </motion.div>
        </div>
    );
}

export default PaymentStatus;
